import Dexie, { type Table } from 'dexie'

// v2.2 findings #70 / #75 item 1: the water tables and the production-table
// copies (`water-*`, `production-unit-table-v2-*`, `productionUnitTableData*`)
// grew with every reading and lived in localStorage, which browsers cap at
// ~5 M characters per origin (shared by every tenant and cache). When it was
// full, writes were silently dropped.
//
// Those keys now live in IndexedDB (quota: GBs), behind an in-memory mirror so
// every existing caller keeps its synchronous read/write API:
// - `hydrateBulkStore()` runs once before the app renders (main.tsx): it loads
//   all rows into memory and moves any of these keys still in localStorage
//   over (removing them from localStorage only after IndexedDB has them).
// - Until hydration succeeds (tests, SSR, IndexedDB unavailable), nothing
//   changes: the storage helpers keep using localStorage.
// - A failed IndexedDB write dispatches `LOCAL_WRITE_FAILED_EVENT`, which the
//   app shell shows as a visible banner instead of a console-only warning.

export const LOCAL_WRITE_FAILED_EVENT = 'sunday:local-write-failed'

export type LocalWriteFailedDetail = { key: string; message: string }

type BulkRow = { key: string; value: string }

class BulkKvDb extends Dexie {
  entries!: Table<BulkRow, string>

  constructor() {
    super('sunday-bulk-kv')
    this.version(1).stores({ entries: 'key' })
  }
}

const BULK_BASE_PREFIXES = ['water-', 'production-unit-table-v2-', 'productionUnitTableData']
// Small per-screen preferences that share the `water-` prefix stay in
// localStorage (they are tiny and read before any dataset).
const NON_BULK_BASE_PREFIXES = ['water-selected-']

const baseOf = (storageKey: string): string => storageKey.split('::')[0] ?? storageKey

export const isBulkStorageKey = (storageKey: string): boolean => {
  const base = baseOf(storageKey)
  if (NON_BULK_BASE_PREFIXES.some((prefix) => base.startsWith(prefix))) return false
  return BULK_BASE_PREFIXES.some((prefix) => base.startsWith(prefix))
}

let db: BulkKvDb | null = null
let memory: Map<string, string> | null = null
let hydration: Promise<boolean> | null = null
let writeChain: Promise<void> = Promise.resolve()

// Other open tabs keep their own in-memory mirror; tell them about every
// change so a tab never writes back a stale copy of a table (localStorage
// reads were always fresh, so this keeps that behaviour).
type BulkChange = { key: string; value: string | null }
let channel: BroadcastChannel | null = null

const openChannel = (): void => {
  if (channel || typeof BroadcastChannel === 'undefined') return
  channel = new BroadcastChannel('sunday-bulk-kv')
  channel.onmessage = (event: MessageEvent<BulkChange>) => {
    const change = event.data
    if (!memory || !change || typeof change.key !== 'string') return
    if (change.value === null) memory.delete(change.key)
    else memory.set(change.key, change.value)
  }
}

const announce = (change: BulkChange): void => {
  try {
    channel?.postMessage(change)
  } catch {
    // ignore: other tabs re-read on their next load
  }
}

export const isBulkStoreReady = (): boolean => memory !== null

const reportFailure = (key: string, error: unknown): void => {
  const message = error instanceof Error ? error.message : String(error)
  console.error(`[bulkStore] Failed to save "${key}" on this device`, error)
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent<LocalWriteFailedDetail>(LOCAL_WRITE_FAILED_EVENT, { detail: { key, message } }))
  }
}

const persist = (key: string, value: string | null): void => {
  const target = db
  if (!target) return
  // Serialize writes so a later value can never be overwritten by an earlier one.
  writeChain = writeChain
    .then(async () => {
      if (value === null) await target.entries.delete(key)
      else await target.entries.put({ key, value })
    })
    .catch((error) => reportFailure(key, error))
}

const listLocalStorageBulkKeys = (): string[] => {
  if (typeof window === 'undefined' || !window.localStorage) return []
  const keys: string[] = []
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i)
    if (key && isBulkStorageKey(key)) keys.push(key)
  }
  return keys
}

/**
 * Load the store into memory and migrate the bulk keys out of localStorage.
 * Resolves true when the store is active, false when the app stays on
 * localStorage (IndexedDB unavailable). Safe to call more than once.
 */
export const hydrateBulkStore = (): Promise<boolean> => {
  if (hydration) return hydration
  hydration = (async () => {
    if (typeof indexedDB === 'undefined') return false
    try {
      const nextDb = new BulkKvDb()
      await nextDb.open()
      const rows = await nextDb.entries.toArray()
      const nextMemory = new Map(rows.map((row) => [row.key, row.value]))

      const legacyKeys = listLocalStorageBulkKeys()
      const migrated: BulkRow[] = []
      for (const key of legacyKeys) {
        const value = window.localStorage.getItem(key)
        if (value === null) continue
        // localStorage is newer only if IndexedDB never had the key.
        if (!nextMemory.has(key)) {
          nextMemory.set(key, value)
          migrated.push({ key, value })
        }
      }
      if (migrated.length > 0) await nextDb.entries.bulkPut(migrated)
      for (const key of legacyKeys) {
        try {
          window.localStorage.removeItem(key)
        } catch {
          // ignore: IndexedDB already holds the value
        }
      }

      db = nextDb
      memory = nextMemory
      openChannel()
      return true
    } catch (error) {
      console.warn('[bulkStore] IndexedDB unavailable; staying on localStorage', error)
      return false
    }
  })()
  return hydration
}

export const bulkGet = (key: string): string | null => memory?.get(key) ?? null

export const bulkSet = (key: string, value: string): void => {
  if (!memory) return
  memory.set(key, value)
  persist(key, value)
  announce({ key, value })
}

export const bulkDelete = (key: string): void => {
  if (!memory) return
  memory.delete(key)
  persist(key, null)
  announce({ key, value: null })
}

export const bulkKeys = (): string[] => (memory ? Array.from(memory.keys()) : [])

/** Resolves once every queued IndexedDB write has settled (tests / shutdown). */
export const flushBulkStoreWrites = (): Promise<void> => writeChain

export const resetBulkStoreForTests = async (): Promise<void> => {
  await writeChain.catch(() => undefined)
  db?.close()
  db = null
  channel?.close()
  channel = null
  memory = null
  hydration = null
  writeChain = Promise.resolve()
}
